import { useCallback, useEffect, useRef, useState } from 'react';
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import ApiService from '@/services/api.service';
import { cn } from '@/lib/utils';
import { UploadIcon, CheckCircle2Icon, XCircleIcon, Loader2Icon } from 'lucide-react';

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  mime_type: string;
}

interface UploadRequestResponse {
  record: UploadedFile;
  signed_upload_url: string;
}

interface S3UploaderProps {
  onUploadComplete: (file: UploadedFile) => void;
  label?: string;
  initUploadEndpoint?: string;
  accept?: string;
}

export function S3Uploader({ onUploadComplete, label = 'Drag and drop a file here, or click to browse', initUploadEndpoint, accept }: S3UploaderProps) {
  const [uploadStatus, setUploadStatus] = useState<null | 'UPLOADING' | 'ERROR' | 'COMPLETE'>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileObjectRef = useRef<UploadedFile | null>(null);
  const onUploadCompleteRef = useRef(onUploadComplete);
  onUploadCompleteRef.current = onUploadComplete;

  // Create Uppy instance inside useEffect so it survives React 18 strict mode.
  // Strict mode runs effects → cleanup → effects again. useMemo only runs once,
  // so the cleanup would destroy the only instance. useEffect re-creates it.
  const uppyRef = useRef<Uppy | null>(null);

  useEffect(() => {
    const instance = new Uppy({
      restrictions: { maxNumberOfFiles: 1 },
      autoProceed: true,
    });

    instance.use(AwsS3, {
      shouldUseMultipart: false,
      getUploadParameters: async (file) => {
        try {
          const response = await ApiService.getInstance().request('POST', initUploadEndpoint || '/files/init-upload', {
            name: file.name,
            size: file.size,
            mime_type: file.type,
          }) as UploadRequestResponse;

          fileObjectRef.current = response.record;
          setFileName(file.name ?? null);
          setUploadStatus('UPLOADING');

          return {
            method: 'PUT' as const,
            url: response.signed_upload_url,
            headers: {
              'Content-Type': file.type as string,
            },
          };
        } catch (ex) {
          console.error('[S3Uploader] Upload init error:', ex);
          setUploadStatus('ERROR');
          setError(ex instanceof Error ? ex.message : 'Failed to initialize upload');
          throw ex;
        }
      },
    });

    instance.on('upload-progress', (_file: any, prog: any) => {
      const bytesTotal = prog.bytesTotal ?? 1;
      setProgress((prog.bytesUploaded / bytesTotal) * 100);
    });

    instance.on('upload-success', () => {
      setUploadStatus('COMPLETE');
      setProgress(100);
      if (fileObjectRef.current) {
        onUploadCompleteRef.current(fileObjectRef.current);
      }
    });

    instance.on('upload-error', (_file: any, err: any) => {
      console.error('[S3Uploader] Upload error:', err);
      setUploadStatus('ERROR');
      setError(err?.message || 'Upload failed');
    });

    uppyRef.current = instance;

    return () => {
      instance.destroy();
      uppyRef.current = null;
    };
  }, []);

  const addFile = useCallback((file: File) => {
    const uppy = uppyRef.current;
    if (!uppy) return;
    try {
      uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
        source: 'local',
      });
    } catch (err) {
      console.error('[S3Uploader] Error adding file:', err);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      addFile(files[0]);
    }
  }, [addFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      addFile(files[0]);
    }
  }, [addFile]);

  if (error && !fileName) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
        <div className="flex items-center gap-2">
          <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
          <p className="text-sm text-destructive m-0">{error}</p>
        </div>
      </div>
    );
  }

  if (fileName) {
    const progressColor = uploadStatus === 'COMPLETE'
      ? 'bg-emerald-500'
      : uploadStatus === 'ERROR'
        ? 'bg-destructive'
        : 'bg-primary';

    return (
      <div className="rounded-lg border border-border p-4 space-y-3">
        <div className="flex items-center gap-2">
          {uploadStatus === 'UPLOADING' && (
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
          )}
          {uploadStatus === 'COMPLETE' && (
            <CheckCircle2Icon className="h-4 w-4 text-emerald-500 flex-shrink-0" />
          )}
          {uploadStatus === 'ERROR' && (
            <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
          )}
          <span className="text-sm font-medium truncate">{fileName}</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-300', progressColor)}
            style={{ width: `${progress}%` }}
          />
        </div>
        {uploadStatus === 'COMPLETE' && (
          <p className="text-xs text-muted-foreground m-0">Upload complete</p>
        )}
        {uploadStatus === 'ERROR' && error && (
          <p className="text-xs text-destructive m-0">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={cn(
        'rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors',
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-muted-foreground/25 hover:border-muted-foreground/50'
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={handleFileChange}
      />
      <UploadIcon className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
      <p className="text-sm text-muted-foreground m-0">{label}</p>
    </div>
  );
}
